import { useEffect, useMemo, useState } from "react";
import { RouteModel } from "../../domain/models/routeModel";
import { useAppDispatch } from "../../../../../hooks/useTypedSelector";
import { updateLayoutForRoute } from "../redux/PlanningThunks";
import { unsetRouteCustom } from "../redux/PlanningSlice";

type SelectedType = { [key: number]: ((number | undefined)[] | null)[] };

export const useRouteTruckLayout = (route: RouteModel) => {
  if (route?.truck?.length === undefined || route?.truck?.width === undefined) {
    return {
      totalRows: 0,
      totalColumns: 0,
      selected: [],
      getSelected: () => [],
      setCustom: () => {
        return;
      },
      setSelected: () => {
        return;
      },
      addEmptyToSelected: () => {
        return;
      },
      addColumn: () => {
        return;
      },
      addRow: () => {
        return;
      },
    };
  }
  const dispatch = useAppDispatch();

  const [selected, _setSelected] = useState<SelectedType>(
    route.layout
      ? JSON.parse(route.layout)
      : Object.fromEntries(
          Array.from({ length: route.truck.length }, (_, index) => [index, []]),
        ),
  );

  const [custom, setCustom] = useState(route.custom ?? false);

  const totalRows = useMemo(() => {
    return Object.keys(selected).length;
  }, [route.truck.length, selected]);

  const totalColumns = useMemo(() => {
    let maxColumnCount = 0;
    Object.values(selected).forEach((column) => {
      if (column.length > maxColumnCount) {
        maxColumnCount = column.length;
      }
    });

    if (route?.truck?.width && maxColumnCount < route.truck.width) {
      return route.truck.width;
    }

    return maxColumnCount;
  }, [selected]);

  const getSelected = (rowIndex: number, columnIndex: number) => {
    // Check if rowIndex exists in selected
    if (!selected[rowIndex]) {
      return [undefined];
    }

    // Check if columnIndex exists in the row
    if (!selected[rowIndex][columnIndex]) {
      return [undefined];
    }
    const selectedValue = selected[rowIndex][columnIndex];

    if (selectedValue == null) {
      return [undefined];
    }

    return selectedValue?.length > 0 ? selectedValue : [undefined];
  };

  const setSelected = (
    rowIndex: number,
    columnIndex: number,
    value: number | undefined,
    index: number,
  ) => {
    dispatch(
      unsetRouteCustom({
        route: route,
        custom: true,
      }),
    );

    _setSelected((prev) => {
      const newState = { ...prev };
      if (!newState[rowIndex]) {
        newState[rowIndex] = [];
      }

      if (newState[rowIndex][columnIndex] == null) {
        newState[rowIndex][columnIndex] = [];
      }

      const col = newState[rowIndex][columnIndex];
      if (col == null) return newState;

      if (value === undefined && col.length > 1) {
        col.splice(index, 1);
      } else {
        col[index] = value;
      }

      return newState;
    });
  };

  useEffect(() => {
    const updatedSelected: SelectedType = {};

    for (const [key, value] of Object.entries(selected)) {
      updatedSelected[parseInt(key)] = value.map((row) => {
        if (row == null) return null;
        return row?.filter((relation) =>
          route.packingSlips.find(
            (packingSlip) =>
              parseInt(packingSlip.relation.id.toString()) === relation,
          ),
        );
      });
    }

    const updatedLayout = JSON.stringify(updatedSelected);
    const prevLayout = route.layout ?? "";
    const prevCustom = route.custom ?? false;

    if (updatedLayout !== prevLayout || custom !== prevCustom) {
      void dispatch(
        updateLayoutForRoute({
          id: route.id,
          layout: updatedLayout,
          custom: custom,
        }),
      );
    }
  }, [selected, custom, route.layout, route.custom, route.packingSlips]);

  const packingSlips = useMemo(() => {
    return (
      route.packingSlips.map((packingSlip) => {
        const relationId = parseInt(packingSlip.relation.id.toString());
        let amount = packingSlip.requests.filter(
          (request) => request.type === "deliver",
        ).length;

        Object.values(selected).forEach((column) => {
          column.forEach((row) => {
            row?.forEach((relation) => {
              if (relation === relationId) {
                amount -= row.length > 1 ? 1 : 2;
              }
            });
          });
        });

        return {
          packingSlip: packingSlip,
          amount: amount > 0 ? amount : 0,
        };
      }) ?? []
    );
  }, [route.packingSlips, selected]);

  const addEmptyToSelected = (rowIndex: number, columnIndex: number) => {
    const selected = getSelected(rowIndex, columnIndex);
    setSelected(rowIndex, columnIndex, undefined, selected.length);
  };

  const addColumn = () => {
    _setSelected((prev) => {
      const newState = { ...prev };
      let maxColumnCount = 0;
      Object.values(newState).forEach((column) => {
        if (column.length > maxColumnCount) {
          maxColumnCount = column.length;
        }
      });

      Object.keys(newState).forEach((key) => {
        const numericKey = parseInt(key);
        const row = newState[numericKey] || [];
        // First initialize to match route.truck.width if empty
        if (row.length === 0 && (route?.truck?.width ?? 0) > 0) {
          for (let i = 0; i < (route?.truck?.width ?? 0); i++) {
            row.push([]);
          }
        } else {
          // Fill any missing rows to match the maximum among columns
          while (row.length < maxColumnCount) {
            row.push([]);
          }
        }
        // Add one more row
        row.push([]);
        newState[numericKey] = row;
      });

      return newState;
    });
    setCustom(true);
  };

  const addRow = () => {
    _setSelected((prev) => {
      const newState = { ...prev };
      newState[totalRows] = [];
      return newState;
    });
    setCustom(true);
  };

  return {
    totalRows,
    totalColumns,
    selected,
    getSelected,
    setSelected,
    addEmptyToSelected,
    packingSlips,
    addColumn,
    addRow,
    setCustom,
  };
};
